import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { configureOrbitClient } from "@orbit/api-client-react";
import { App } from "./App";
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  setSessionExpiredFlag,
  updateStoredTokens,
} from "./lib/auth";
import "./index.css";

/**
 * Self-heal escape hatch for users with a stale service worker still
 * installed.
 *
 * Background: this customer PWA registers a service worker at the
 * origin root with default scope `/`. Older builds did not configure
 * `navigateFallbackDenylist`, so the SW would intercept navigations to
 * `/admin/*` (the superadmin Console, served by a separate Vite bundle)
 * and serve the cached customer-app `index.html` instead of letting the
 * request reach the network. The result: the customer React app would
 * mount on a `/admin/<route>` URL it doesn't recognize and render a
 * blank page.
 *
 * The current vite.config.ts adds the denylist plus `skipWaiting` and
 * `clientsClaim` so a freshly-served SW takes over immediately. But for
 * users who already have the OLD SW installed in their browser, the
 * stale SW still controls the very page load that's supposed to install
 * the fix — so they see one last blank `/admin/*` page before the new
 * SW activates. This block detects that exact situation: if the
 * customer entry script is running on a `/admin/*` URL, it means the
 * stale SW served us by mistake. Unregister every service worker on the
 * origin and hard-reload so the next request goes straight to the
 * network and hits the server's `/admin{/*splat}` admin handler.
 *
 * After one such auto-rescue the user lands on the real admin shell and
 * future navigations route correctly. Brand-new visitors never see this
 * code path because a fresh SW install already includes the denylist.
 */
if (
  typeof window !== "undefined" &&
  window.location.pathname.startsWith("/admin")
) {
  const reload = (): void => {
    // Replace history entry so the back button doesn't loop the user
    // back into the broken state.
    window.location.replace(window.location.href);
  };
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker
      .getRegistrations()
      .then((regs) => Promise.all(regs.map((r) => r.unregister())))
      .then(() => caches?.keys?.() ?? [])
      .then((keys) =>
        Promise.all((keys as string[]).map((k) => caches.delete(k))),
      )
      .catch(() => undefined)
      .finally(reload);
  } else {
    reload();
  }
  // Bail out before mounting React so the customer app never appears
  // on /admin/* URLs.
  throw new Error(
    "[orbit] Customer PWA loaded on /admin/* — self-healing service worker; reloading…",
  );
}

/**
 * Auto-reload on service-worker takeover (D89).
 *
 * Background. The SW (`src/sw.ts`) uses `skipWaiting()` +
 * `clientsClaim()` so a freshly-deployed SW becomes the active
 * controller immediately on activation. That correctly serves the new
 * precached bundle for the NEXT navigation/fetch, but the page that's
 * currently rendered keeps running the OLD JS already in memory. A
 * user with the PWA open will keep hitting the stale code (e.g. the
 * pre-D88 QR scanner that truncated `artehaus-ph` → `artehaus`) until
 * they manually refresh.
 *
 * The fix below listens for the `controllerchange` event — fired
 * exactly once when a new SW takes control of an existing client —
 * and force-reloads the page so the new bundle loads from the fresh
 * precache. Guarded by `refreshing` to coalesce the unlikely double-
 * fire, and gated on `navigator.serviceWorker.controller` being
 * non-null at startup so we don't reload on the very first SW install
 * (where there's no "old" controller to replace).
 *
 * Trade-off. A user mid-action (e.g. typing a long form) loses one
 * reload cycle on every deploy that ships SPA changes. We accept that
 * because: (a) deploys are rare and bug-fix-driven, (b) a reload is
 * less disruptive than a stale bundle silently producing wrong
 * results, and (c) we don't yet have a "new version available" toast
 * UI that would let the user choose when to reload.
 */
if (
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  navigator.serviceWorker.controller
) {
  let refreshing = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (refreshing) return;
    refreshing = true;
    window.location.reload();
  });
}

/**
 * D93 — Aggressive service-worker registration.
 *
 * Replaces the bare `registerSW.js` that vite-plugin-pwa would
 * otherwise inject (we set `injectRegister: false` in vite.config.ts).
 * Three deliberate departures from the default registration:
 *
 *   1. `updateViaCache: "none"` — when the browser fetches `/sw.js`
 *      to check for an update, it MUST bypass the HTTP cache.
 *      Combined with the `Cache-Control: no-cache, must-revalidate`
 *      header on sw.js (D91) this guarantees the etag check fires
 *      on every update poll.
 *
 *   2. `registration.update()` polled every 60 s. Without this, the
 *      browser only checks for a new sw.js on its own lazy schedule
 *      (typically when navigating, but throttled and capped at 24 h
 *      for installed PWAs). The previous registerSW.js had no poll
 *      at all, so users on the home-screen-installed PWA could run
 *      a stale bundle for hours after a deploy — exactly the
 *      "blank screen on QR scan" symptom (D90).
 *
 *   3. `visibilitychange` → `update()`. When the user backgrounds
 *      and re-foregrounds the PWA (very common pattern: scan QR →
 *      switch to camera app → switch back), check for a new SW
 *      immediately rather than waiting up to 60 s for the next poll.
 *
 * The SW itself already does `skipWaiting()` + `clientsClaim()` on
 * install/activate, and the `controllerchange` listener above
 * reloads the page when the new SW takes over. End-to-end: a new
 * deploy propagates to every open client within ~60 s, no manual
 * refresh required.
 */
if (typeof window !== "undefined" && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js", { scope: "/", updateViaCache: "none" })
      .then((registration) => {
        // Periodic update poll. 60 s is a balance between catching
        // a new deploy quickly and not hammering the origin from
        // every open tab. The /sw.js response is tiny and gated by
        // an etag check so most polls are a 304.
        const POLL_MS = 60_000;
        const poll = (): void => {
          registration.update().catch(() => undefined);
        };
        setInterval(poll, POLL_MS);

        // Re-check on tab foreground so the user who just unlocked
        // their phone doesn't have to wait for the next 60 s tick.
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") poll();
        });
      })
      .catch(() => {
        // Registration failures are non-fatal — the app still works
        // online, just without offline / push support. Logging here
        // would be visible in the deploy logs but adds no actionable
        // signal, so we swallow.
      });
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, refetchOnWindowFocus: false },
  },
});

/**
 * Silent refresh: when an authenticated request returns 401, this hook
 * trades the stored refresh token for a new access token. We deduplicate
 * concurrent calls so a burst of 401s only triggers one refresh round.
 */
let inflightRefresh: Promise<string | null> | null = null;
async function tryRefreshAccessToken(): Promise<string | null> {
  if (inflightRefresh) return inflightRefresh;
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;
  inflightRefresh = (async () => {
    try {
      const res = await fetch("/api/v1/auth/refresh", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return null;
      const json = (await res.json()) as { data?: { tokens?: { accessToken: string; refreshToken: string; accessTokenExpiresAt: string; refreshTokenExpiresAt: string } } };
      const tokens = json.data?.tokens;
      if (!tokens?.accessToken) return null;
      updateStoredTokens(tokens);
      return tokens.accessToken;
    } catch {
      return null;
    } finally {
      inflightRefresh = null;
    }
  })();
  return inflightRefresh;
}

configureOrbitClient({
  baseUrl: "",
  getAccessToken: () => getAccessToken(),
  tryRefreshAccessToken,
  onUnauthorized: () => {
    // Refresh has already been attempted by the request layer; reaching
    // here means we genuinely lost the session. Flag it for the Login
    // page so the user gets a friendly banner instead of a blank screen.
    setSessionExpiredFlag();
    clearTokens();
  },
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
